import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Alert, Card, Spin, Tag, Text, useLocale, type IconName } from "@app/ui";
import { Link } from "react-router-dom";
import { ErrorAlert } from "../errors/index.js";
import { getOrgStats, getScanStats, listExpiringCertifications } from "../api/dpp.js";
import type { Certification, ScanDayCount } from "../api/dpp.js";
import { useT } from "../i18n.js";
import { NoOrgState } from "../orgs/NoOrgState.js";
import { useOrgs } from "../orgs/OrgContext.js";
import type { MessageKey } from "../i18n.js";
import type { OrgStats } from "../types.js";
import { Donut, type DonutSegment } from "../dashboard/Donut.js";
import { BarChart } from "../dashboard/BarChart.js";
import { QuotaMeter } from "../dashboard/QuotaMeter.js";
import { StatDot, StatTile, type StatTone } from "../dashboard/StatTile.js";
import "../dashboard/charts.css";

interface Tile {
  label: MessageKey;
  icon: IconName;
  /** Identity hue for the chip + rail (see StatTile / charts.css). */
  tone: StatTone;
  value: number;
  note: ReactNode;
}

export function DashboardPage() {
  const t = useT();
  const { locale } = useLocale();
  const { org, loading: orgsLoading } = useOrgs();

  const [stats, setStats] = useState<OrgStats | null>(null);
  const [expiring, setExpiring] = useState<Certification[]>([]);
  const [scans, setScans] = useState<ScanDayCount[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const nf = useMemo(() => new Intl.NumberFormat(locale), [locale]);
  const monthFmt = useMemo(() => new Intl.DateTimeFormat(locale, { month: "short" }), [locale]);
  const shortMonth = (month: string): string => {
    const [y, m] = month.split("-").map(Number);
    return monthFmt.format(new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, 1)));
  };

  useEffect(() => {
    if (!org) return;
    let alive = true;
    setLoading(true);
    setError(null);
    setStats(null);
    getOrgStats(org.id)
      .then((s) => alive && setStats(s))
      .catch((e) => alive && setError(e))
      .finally(() => alive && setLoading(false));
    // Best-effort compliance nudge — a failure here must not break the dashboard.
    listExpiringCertifications(org.id, 90)
      .then((c) => alive && setExpiring(c))
      .catch(() => undefined);
    getScanStats(org.id, 30)
      .then((s) => alive && setScans(s))
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [org?.id]);

  if (orgsLoading) return <Spin />;
  if (!org) return <NoOrgState />;
  if (error && !stats) return <ErrorAlert error={error} />;
  if (loading || !stats)
    return (
      <div style={{ display: "grid", placeItems: "center", minHeight: 240 }}>
        <Spin />
      </div>
    );

  // Icons name the entity (a box is a product, a page is a passport) — the old
  // funnel/tick glyphs described nothing. Notes that quote a status carry that
  // status' dot, so the tile row and the donut legends read as one palette.
  const tiles: Tile[] = [
    {
      label: "dashboard.tileProducts",
      icon: "box",
      tone: "indigo",
      value: stats.products.total,
      note: (
        <>
          <StatDot tone="live" />
          {`${nf.format(stats.products.byStatus.active)} ${t("productStatus.active")}`}
        </>
      ),
    },
    {
      label: "dashboard.tilePublished",
      icon: "eye",
      tone: "teal",
      value: stats.passports.byStatus.published,
      note: t("dashboard.publicQrPages"),
    },
    {
      label: "dashboard.tilePassportsTotal",
      icon: "file-text",
      tone: "violet",
      value: stats.passports.total,
      note: (
        <>
          <StatDot tone="draft" />
          {`${nf.format(stats.passports.byStatus.draft)} ${t("passportStatus.draft")}`}
        </>
      ),
    },
    {
      label: "dashboard.tileCarriers",
      icon: "grid",
      tone: "coral",
      value: stats.identifiers.total,
      note: t("dashboard.carriersNote"),
    },
  ];

  const passportSegments: DonutSegment[] = [
    {
      key: "published",
      label: t("passportStatus.published"),
      value: stats.passports.byStatus.published,
      color: "var(--chart-live)",
    },
    {
      key: "draft",
      label: t("passportStatus.draft"),
      value: stats.passports.byStatus.draft,
      color: "var(--chart-draft)",
    },
    {
      key: "archived",
      label: t("passportStatus.archived"),
      value: stats.passports.byStatus.archived,
      color: "var(--chart-archived)",
    },
  ];

  const productSegments: DonutSegment[] = [
    {
      key: "active",
      label: t("productStatus.active"),
      value: stats.products.byStatus.active,
      color: "var(--chart-live)",
    },
    {
      key: "draft",
      label: t("productStatus.draft"),
      value: stats.products.byStatus.draft,
      color: "var(--chart-draft)",
    },
    {
      key: "archived",
      label: t("productStatus.archived"),
      value: stats.products.byStatus.archived,
      color: "var(--chart-archived)",
    },
  ];

  const barData = stats.issuance.map((m) => ({
    month: m.month,
    label: shortMonth(m.month),
    count: m.count,
  }));

  // The billing-plan label carries a verbose "(price, allowance)" suffix that
  // overflowed the card header on narrow/3-up layouts. The header badge only
  // needs the plan NAME — the allowance is already spelled out by the quota
  // meter below it — so trim the parenthetical for the pill.
  const planName = t(`billingPlan.${stats.usage.billingPlan}`).split(" (")[0];

  return (
    <div className="dpp-dash">
      {expiring.length > 0 && (
        <Alert type="warning">
          {t("dashboard.expiringCerts")}{" "}
          {expiring.map((c) => `${c.scheme} (${c.validUntil?.slice(0, 10) ?? ""})`).join(", ")} —{" "}
          <Link to="/facilities">{t("dashboard.expiringCertsLink")}</Link>
        </Alert>
      )}
      <div className="dpp-kpis">
        {tiles.map((tile, i) => (
          <StatTile
            key={tile.label}
            label={t(tile.label)}
            value={nf.format(tile.value)}
            icon={tile.icon}
            tone={tile.tone}
            note={tile.note}
            index={i}
          />
        ))}
      </div>

      <div className="dpp-charts-3up">
        <Card title={t("dashboard.passportStatus")}>
          <Donut
            segments={passportSegments}
            title={t("dashboard.passportStatus")}
            caption={t("dashboard.donutPassports")}
            formatNumber={(v) => nf.format(v)}
          />
        </Card>
        <Card title={t("dashboard.productStatus")}>
          <Donut
            segments={productSegments}
            title={t("dashboard.productStatus")}
            caption={t("dashboard.donutProducts")}
            formatNumber={(v) => nf.format(v)}
          />
        </Card>
        <Card title={t("dashboard.usage")} extra={<Tag color="primary">{planName}</Tag>}>
          <QuotaMeter
            used={stats.usage.viewsThisYear}
            limit={stats.usage.includedViewsPerYear}
            label={t("dashboard.viewsThisYear")}
            unlimitedLabel={t("billing.unlimited")}
            formatNumber={(v) => nf.format(v)}
          />
          {stats.usage.maxProducts !== null && (
            <QuotaMeter
              used={stats.products.total}
              limit={stats.usage.maxProducts}
              label={t("dashboard.productAllowance")}
              unlimitedLabel={t("billing.unlimited")}
              formatNumber={(v) => nf.format(v)}
            />
          )}
        </Card>
      </div>

      <div className="dpp-charts-2up">
        <Card title={t("dashboard.issuance")}>
          <div style={{ marginBottom: "var(--az-sp-3)" }}>
            <Text small secondary>
              {t("dashboard.issuanceHint")}
            </Text>
          </div>
          <BarChart data={barData} title={t("dashboard.issuance")} formatNumber={(v) => nf.format(v)} />
        </Card>
        {scans.length > 0 && (
          <Card title={t("dashboard.scans")}>
            <div style={{ marginBottom: "var(--az-sp-3)" }}>
              <Text small secondary>
                {t("dashboard.scansHint")}
              </Text>
            </div>
            <BarChart
              data={scans.map((s) => ({ month: s.day, label: s.day.slice(5), count: s.count }))}
              title={t("dashboard.scans")}
              formatNumber={(v) => nf.format(v)}
            />
          </Card>
        )}
      </div>
    </div>
  );
}
